import {
  assignPrototypeAPIs,
  isFunction,
  type CellData,
  type Column_Internal,
  type ColumnDef,
  type Header,
  type Row,
  type RowData,
  type SortingState,
  type StockFeatures,
  type TableFeature,
  type TableFeatures,
  type TableOptions_All,
} from '@tanstack/react-table';
import {
  column_getCanMultiSort,
  column_getIsSorted,
  table_getColumn,
  table_setSorting,
} from '@tanstack/react-table/static-functions';

import {
  datelikeCompare,
  numberlikeCompare,
  stringlikeCompare,
  stringlikeCompareCaseSensitive,
} from './compare-functions.js';
import type {
  DataTableCustomSortingFn,
  RowSortingFeatureConstructors,
} from './sorting-types.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';

/**
 * Typeguard function that checks whether the given column definition has a 'sortAccessor' defined.
 */
function isColumnDefWithSortAccessor<
  TFeature extends TableFeatures,
  TData extends RowData,
>(columnDef: unknown): columnDef is ColumnDef<TFeature, TData> {
  return (
    columnDef !== undefined &&
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'sortAccessor' in columnDef &&
    columnDef.sortAccessor !== undefined
  );
}

const sortingFunctions = {
  text: stringlikeCompare,
  textCaseSensitive: stringlikeCompareCaseSensitive,
  number: numberlikeCompare,
  datetime: datelikeCompare,
};

/**
 * Helper function to get the aria-sort attribute value for a header based on its sorting state.
 * @internal
 */
export function header_getAriaSortAttribute<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TFeatures, TData, TValue>,
): 'ascending' | 'descending' | 'none' | undefined {
  if (
    // Requires cast to push through the generic constraints on the UnionToIntersection generic.
    (header.table.options as TableOptions_All<StockFeatures, TData>)
      .enableSorting &&
    // Requires cast to push through the generic constraints on the UnionToIntersection generic.
    (header.column.columnDef as ColumnDef<StockFeatures, TData>)
      .enableSorting !== false &&
    header.column.columns.length === 0
  ) {
    const isSorted = column_getIsSorted(header.column);
    if (isSorted) {
      return isSorted === 'asc' ? 'ascending' : 'descending';
    }
    return 'none';
  }
  return undefined;
}

/**
 * Helper function to determine if sorting can be triggered by clicking on the header, based on the header and column properties.
 * @internal
 */
export function header_getCanSortByHeaderClick<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(header: Header<TFeatures, TData, TValue>): boolean {
  if (header.column.getCanColumnActions() === true) {
    return false;
  }
  return column_getCanSort(header.column);
}

/**
 * Helper function to get the sorting value for a given row and column, utilizing caching for performance optimization.
 * @internal
 */
export function row_getSortingValue<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>, columnId: string): undefined | unknown {
  const column = table_getColumn(row.table, columnId);

  if (column && isColumnDefWithSortAccessor(column.columnDef)) {
    const sortAccessor = column.columnDef.sortAccessor;
    if (sortAccessor && typeof sortAccessor === 'string') {
      return (
        sortAccessor
          .split('.')
          // Need to cast to unknown first to satisfy the compiler,
          // as it doesn't know that the reduce will return the correct type based
          // on the sortAccessor string.
          .reduce<unknown>(
            (obj, key) => (obj as unknown as Record<string, unknown>)[key],
            row.original,
          )
      );
    } else if (typeof sortAccessor === 'function') {
      return sortAccessor(row.original);
    }
  }

  if (!column?.accessorFn) {
    return undefined;
  }

  return column.accessorFn(row.original, row.index);
}

export function column_getCustomSortingFn<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column_Internal<TFeatures, TData, TValue>,
): DataTableCustomSortingFn<TValue> {
  if (!column) {
    throw new Error();
  }

  if (isFunction(column.columnDef.customSortingFn)) {
    return column.columnDef.customSortingFn;
  }

  if (
    typeof column.columnDef.sortType === 'string' &&
    sortingFunctions[column.columnDef.sortType]
  ) {
    return sortingFunctions[
      column.columnDef.sortType
    ] as DataTableCustomSortingFn<TValue>;
  }

  return sortingFunctions['text'] as DataTableCustomSortingFn<TValue>;
}

/**
 * Override the default getFirstSortDir
 * @internal
 */
export function column_getFirstSortDir<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  const sortDescFirst =
    column.columnDef.sortDescFirst ?? column.table.options.sortDescFirst;
  return sortDescFirst ? 'desc' : 'asc';
}

/**
 * Override the default getNextSortingOrder as the original implementation calls column_getAutoSortDir from tanstack/table-core directly without a chance on our end to override.
 * @internal
 */
export function column_getNextSortingOrder<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>, multi?: boolean) {
  const firstSortDirection = column_getFirstSortDir(column);
  const isSorted = column_getIsSorted(column);

  if (!isSorted) {
    return firstSortDirection;
  }

  if (
    isSorted !== firstSortDirection &&
    (column.table.options.enableSortingRemoval ?? true) && // If enableSortRemove, enable in general
    (multi ? column.table.options.enableMultiRemove ?? true : true) // If multi, don't allow if enableMultiRemove))
  ) {
    return false;
  }
  return isSorted === 'desc' ? 'asc' : 'desc';
}

/**
 * Overrides the default auto sort direction to always be ascending for the first sort, instead of toggling between ascending and descending.
 *
 * @internal
 */
export function column_getAutoSortDir<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(column: Column_Internal<TFeatures, TData>): 'asc' {
  // overwrite the default from react table
  // by default all columns should be sorted ascending first
  return 'asc';
}

/**
 * Overrides the default column_toggleSorting to allow toggling the sorting state by clicking on the header, while ensuring that interactions with interactive elements within the header do not trigger sorting.
 * Override is necessary as tanstack/table-core's default toggleSorting implementation does call column_getNextSortingOrder from tanstack internally and not the one we provide on the instance.
 * @internal
 */
export function column_toggleSorting<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  column: Column_Internal<TFeatures, TData, TValue>,
  desc?: boolean,
  multi?: boolean,
) {
  // this needs to be outside of table.setSorting to be in sync with rerender
  const nextSortingOrder = column_getNextSortingOrder(column);

  if (!multi) {
    const oldSorting = column.table.atoms.sorting?.get();

    if (oldSorting && oldSorting.length > 1) {
      const existingIndex = oldSorting.findIndex((s) => s.id === column.id);

      // In multi-sort mode, plain-clicking a sorted column should clear all other sorted columns
      // and keep only the clicked column, with its direction cycled to the next value.
      // Out-of-the-box Tanstack only does this if the clicked column is not the last in the sorting array for some reason,
      // which is why we need to override the function for specific case where the clicked column is the last in the sorting array.
      if (existingIndex === oldSorting.length - 1) {
        const nextDesc = desc ?? nextSortingOrder === 'desc';
        table_setSorting(column.table, [{ id: column.id, desc: nextDesc }]);
        return;
      }
    }
  }

  const hasManualValue = typeof desc !== 'undefined';

  table_setSorting(column.table, (old) => {
    // Find any existing sorting for this column
    const existingSorting = old.find((d) => d.id === column.id);
    const existingIndex = old.findIndex((d) => d.id === column.id);

    let newSorting: SortingState = [];

    // What should we do with this sort action?
    let sortAction: 'add' | 'remove' | 'toggle' | 'replace';
    const nextDesc = hasManualValue ? desc : nextSortingOrder === 'desc';

    // Multi-mode
    if (old.length && column_getCanMultiSort(column) && multi) {
      if (existingSorting) {
        sortAction = 'toggle';
      } else {
        sortAction = 'add';
      }
    } else {
      // Normal mode
      if (old.length && existingIndex !== old.length - 1) {
        sortAction = 'replace';
      } else if (existingSorting) {
        sortAction = 'toggle';
      } else {
        sortAction = 'replace';
      }
    }

    // Handle toggle states that will remove the sorting
    if (sortAction === 'toggle') {
      // If we are "actually" toggling (not a manual set value), should we remove the sorting?
      if (!hasManualValue) {
        // Is our intention to remove?
        if (!nextSortingOrder) {
          sortAction = 'remove';
        }
      }
    }

    if (sortAction === 'add') {
      newSorting = [
        ...old,
        {
          id: column.id,
          desc: nextDesc,
        },
      ];
      // Take latest n columns
      newSorting.splice(
        0,
        newSorting.length -
          (column.table.options.maxMultiSortColCount ??
            Number.MAX_SAFE_INTEGER),
      );
    } else if (sortAction === 'toggle') {
      // This flips (or sets) the
      newSorting = old.map((d) => {
        if (d.id === column.id) {
          return {
            ...d,
            desc: nextDesc,
          };
        }
        return d;
      });
    } else if (sortAction === 'remove') {
      newSorting = old.filter((d) => d.id !== column.id);
    } else {
      newSorting = [
        {
          id: column.id,
          desc: nextDesc,
        },
      ];
    }

    return newSorting;
  });
}

/**
 * Overrides the default column_getToggleSortingHandler
 * Override is necessary as tanstack/table-core's default implementation does call `column_toggleSorting` from tanstack internally and not the one we provide on the instance.
 */
export function column_getToggleSortingHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  const canSort = column_getCanSort(column);

  return (e: unknown) => {
    if (!canSort) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e as any).persist?.();
    column_toggleSorting(
      column,

      undefined,
      column_getCanMultiSort(column)
        ? column.table.options.isMultiSortEvent?.(e)
        : false,
    );
  };
}

/**
 * Helper function to determine if a column is sortable based on its definition and the table's sorting configuration.
 * @internal
 */
export function column_getCanSort<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column?: Column_Internal<TFeatures, TData, TValue>): boolean {
  if (column === undefined) {
    return false;
  }
  // Grouped columns should for now not be sortable.
  if (isGroupColumnDef<TFeatures, TData>(column.columnDef)) {
    return false;
  }
  return (
    (column.columnDef.enableSorting ?? true) &&
    (column.table.options.enableSorting ?? false) &&
    !!column.accessorFn
  );
}

/**
 * Constructs the column row sorting for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowSortingFeature<
  TData extends RowData,
>(): TableFeature<RowSortingFeatureConstructors<TData>> {
  return {
    assignHeaderPrototype: (prototype, table) => {
      assignPrototypeAPIs('rowSortingFeature', prototype, table, {
        header_getAriaSortAttribute: {
          fn: (header) => header_getAriaSortAttribute(header),
        },
        header_getCanSortByHeaderClick: {
          fn: (header) => header_getCanSortByHeaderClick(header),
        },
      });
    },
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('rowSortingFeature', prototype, table, {
        row_getSortingValue: {
          fn: (row, columnId) => row_getSortingValue(row, columnId),
        },
      });
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('rowSortingFeature', prototype, table, {
        column_getCustomSortingFn: {
          fn: (column) => column_getCustomSortingFn(column),
        },
        column_getAutoSortDir: {
          fn: (column) => column_getAutoSortDir(column),
        },
        column_getCanSort: { fn: (column) => column_getCanSort(column) },
        column_getFirstSortDir: {
          fn: (column) => column_getFirstSortDir(column),
        },
        column_getNextSortingOrder: {
          fn: (column, multi) => column_getNextSortingOrder(column, multi),
        },
        column_toggleSorting: {
          fn: (column, desc, multi) =>
            column_toggleSorting(column, desc, multi),
        },
        column_getToggleSortingHandler: {
          fn: (column) => column_getToggleSortingHandler(column),
        },
      });
    },
  };
}

/**
 * Feature implementation for Row Sorting.
 * @internal
 */
export const DataTableRowSortingFeature = constructRowSortingFeature();
